import { matchesChannelId } from '../../products/helpers/channelHelpers';
import { findListingEntity } from '../../products/helpers/entityReadHelpers';

const getListingVariantEntity = (productsState, listingVariantId) => (
  productsState?.entities?.listingVariants?.[listingVariantId] ?? null
);

const getMasterVariant = (masterProduct, masterVariantId) => (
  (masterProduct?.variants || []).find(
    ({ _id }) => String(_id) === String(masterVariantId),
  ) ?? null
);

export const buildMasterVariantLink = (masterProduct, variantId) => {
  const variant = (masterProduct?.variants || []).find(
    ({ _id }) => String(_id) === String(variantId),
  );

  if (!variant) return null;

  return {
    variantId: variant._id,
    productId: masterProduct._id,
    title: variant.title || variant.displayName || variant._id,
    type: 'masterVariant',
  };
};

export const buildListingGalleryLink = ({
  listingId,
  listingProduct,
  masterProductId,
  salesChannel,
  isDraft = true,
}) => {
  if (!listingId || !listingProduct) return null;

  return {
    _id: listingId,
    title: listingProduct.title || listingId,
    type: 'listing',
    ...(masterProductId ? { masterProductId } : {}),
    ...(salesChannel ? { salesChannel } : {}),
    ...(isDraft ? { isDraft: true } : {}),
  };
};

export const buildListingVariantLink = ({
  masterProduct,
  productsState,
  channelId,
  listingId,
  listingVariantId,
  isDraft = true,
}) => {
  const listingVariant = getListingVariantEntity(productsState, listingVariantId);

  if (listingVariant?.masterVariantId) {
    const masterVariant = getMasterVariant(masterProduct, listingVariant.masterVariantId);

    return {
      variantId: listingVariantId,
      productId: listingId,
      title: listingVariant.title || masterVariant?.title || masterVariant?.displayName || listingVariantId,
      type: 'listingVariant',
      masterProductId: masterProduct._id,
      masterVariantId: listingVariant.masterVariantId,
      salesChannel: channelId,
      ...(isDraft ? { isDraft: true } : {}),
    };
  }

  const masterVariant = (masterProduct?.variants || []).find((variant) => (
    (variant.channels || []).some(
      (channel) => matchesChannelId(channel, channelId)
        && String(channel._id) === String(listingVariantId),
    )
  ));

  const channelVariant = masterVariant?.channels?.find(
    (channel) => String(channel._id) === String(listingVariantId),
  );

  if (!masterVariant) return null;

  return {
    variantId: listingVariantId,
    productId: listingId,
    title: channelVariant?.title || masterVariant?.title || listingVariantId,
    type: 'listingVariant',
    masterProductId: masterProduct._id,
    masterVariantId: masterVariant._id,
    salesChannel: channelId,
    ...(isDraft ? { isDraft: true } : {}),
  };
};

export const buildListingAttachStoreLinksFromResponse = ({
  listing,
  masterProduct,
  masterProductId,
  updatedListing,
  updatedVariants = [],
  isDraft = true,
}) => {
  const listingId = updatedListing?._id || listing.listingId;
  const channelId = listing.channelId;
  const resolvedMasterProductId = masterProductId || masterProduct?._id;

  const product = listing.listingGallery && listingId
    ? buildListingGalleryLink({
      listingId,
      listingProduct: updatedListing || { _id: listingId },
      masterProductId: resolvedMasterProductId,
      salesChannel: channelId || updatedListing?.salesChannelId || updatedListing?.salesChannel,
      isDraft,
    })
    : null;

  const requestedVariantIds = new Set((listing.listingVariantIds || []).map(String));
  const productVariants = (updatedVariants || [])
    .filter(({ _id }) => requestedVariantIds.has(String(_id)))
    .map((listingVariant) => {
      const masterVariant = getMasterVariant(masterProduct, listingVariant.masterVariantId);

      return {
        variantId: listingVariant._id,
        productId: listingId,
        title: listingVariant.title || masterVariant?.title || listingVariant._id,
        type: 'listingVariant',
        masterProductId: resolvedMasterProductId,
        masterVariantId: listingVariant.masterVariantId,
        salesChannel: channelId,
        ...(isDraft ? { isDraft: true } : {}),
      };
    })
    .filter((variant) => variant.masterVariantId);

  return { product, productVariants };
};

export const buildListingAttachStoreLinks = ({
  masterProduct,
  listing,
  productsState,
  isDraft = true,
}) => {
  const { listingId } = listing;
  let listingProduct = findListingEntity(productsState, {
    masterProductId: masterProduct?._id,
    channelId: listing.channelId,
    listingProductId: listingId,
  });

  if (!listingProduct) {
    const channel = (masterProduct?.channels || []).find(
      (entry) => matchesChannelId(entry, listing.channelId),
    );
    listingProduct = channel?.listingProduct;
  }

  const product = listing.listingGallery
    ? buildListingGalleryLink({
      listingId,
      listingProduct,
      masterProductId: masterProduct?._id,
      salesChannel: listing.channelId || listingProduct?.salesChannelId || listingProduct?.salesChannel,
      isDraft,
    })
    : null;

  const productVariants = (listing.listingVariantIds || [])
    .map((listingVariantId) => buildListingVariantLink({
      masterProduct,
      productsState,
      channelId: listing.channelId,
      listingId,
      listingVariantId,
      isDraft,
    }))
    .filter(Boolean);

  return { product, productVariants };
};
